import { useEffect, useState } from "react";
import { Wand2, Loader2, Copy, CheckCheck, Printer, FileDown, Users } from "lucide-react";
import { generateLessonPlan, fetchMyClasses } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { MD } from "@/components/MD";
import { exportLessonPlanPdf } from "@/lib/pdf";
import PaywallModal from "@/components/PaywallModal";

export default function Teach() {
    const { lang, t } = useLang();
    const { isTeacher } = useAuth();
    const [topic, setTopic] = useState("");
    const [grade, setGrade] = useState("9-10");
    const [duration, setDuration] = useState(60);
    const [style, setStyle] = useState("hands-on");
    const [classId, setClassId] = useState("");
    const [myClasses, setMyClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [plan, setPlan] = useState(null);
    const [copied, setCopied] = useState(false);
    const [paywall, setPaywall] = useState(null);

    useEffect(() => {
        if (isTeacher) {
            fetchMyClasses().then(setMyClasses).catch(() => {});
        }
    }, [isTeacher]);

    const generate = async (e) => {
        e.preventDefault();
        if (!topic.trim()) return;
        setLoading(true);
        setPlan(null);
        try {
            const data = await generateLessonPlan({
                topic: topic.trim(),
                grade_level: grade,
                duration_min: Number(duration) || 60,
                learning_style: style,
                lang,
                class_id: classId || undefined,
            });
            setPlan(data);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 402 && detail?.code === "paywall_lesson_plan_limit") {
                setPaywall({
                    feature:
                        lang === "es"
                            ? `Más de ${detail.limit} planes AI por mes`
                            : `More than ${detail.limit} AI lesson plans / month`,
                });
            } else {
                alert("Generation failed — try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const copy = () => {
        if (!plan) return;
        navigator.clipboard.writeText(plan.plan_markdown);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <main
            className="max-w-6xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-24 relative"
            data-testid="teach-page"
        >
            <div className="glow glow-coral -top-20 right-0" />
            <div className="relative z-10">
                <span className="tag">{t.teach.tag}</span>
                <h1 className="font-display font-black text-white text-5xl md:text-7xl mt-4 uppercase leading-[0.92]">
                    {t.teach.head_a}
                    <br />
                    <span className="font-brush text-[var(--lime)] inline-block -rotate-3">
                        {t.teach.head_b}
                    </span>
                </h1>
                <p className="font-mono text-[var(--muted)] mt-5 max-w-2xl">
                    {t.teach.blurb}{" "}
                    {!isTeacher && (
                        <span className="text-[var(--cyan)]">
                            (Tip: log in as a teacher to save your generated plans
                            to a dashboard.)
                        </span>
                    )}
                </p>
            </div>

            <div className="grid grid-cols-12 gap-6 md:gap-10 mt-12 relative z-10">
                <form
                    onSubmit={generate}
                    className="col-span-12 md:col-span-5 terminal p-6 no-print"
                    data-testid="lesson-form"
                >
                    <div className="terminal-header -mx-6 -mt-6 px-6 py-3 flex items-center gap-2 mb-5">
                        <span className="w-2 h-2 rounded-full bg-[var(--coral)] animate-pulse" />
                        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--coral)]">
                            {t.teach.inputHeader}
                        </span>
                    </div>
                    <Field label={t.teach.topic}>
                        <input
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder={t.teach.topicPh}
                            className="input-line"
                            data-testid="input-topic"
                            required
                        />
                    </Field>
                    <Field label={t.teach.grade}>
                        <select
                            value={grade}
                            onChange={(e) => setGrade(e.target.value)}
                            className="input-line"
                            data-testid="input-grade"
                        >
                            {Object.entries(t.teach.grades).map(([k, v]) => (
                                <option key={k} className="bg-black" value={k}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label={t.teach.duration}>
                        <input
                            type="number"
                            min={20}
                            max={180}
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            className="input-line"
                            data-testid="input-duration"
                        />
                    </Field>
                    <Field label={t.teach.style}>
                        <select
                            value={style}
                            onChange={(e) => setStyle(e.target.value)}
                            className="input-line"
                            data-testid="input-style"
                        >
                            {Object.entries(t.teach.styles).map(([k, v]) => (
                                <option key={k} className="bg-black" value={k}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </Field>

                    {isTeacher && myClasses.length > 0 && (
                        <label className="block mb-4">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--coral)] flex items-center gap-1">
                                <Users size={11} />
                                {lang === "es"
                                    ? "Compartir con clase (opcional)"
                                    : "Share with class (optional)"}
                            </span>
                            <select
                                value={classId}
                                onChange={(e) => setClassId(e.target.value)}
                                className="input-line mt-1"
                                data-testid="input-share-class"
                            >
                                <option value="" className="bg-black">
                                    {lang === "es" ? "— privado —" : "— private —"}
                                </option>
                                {myClasses.map((c) => (
                                    <option
                                        key={c.id}
                                        value={c.id}
                                        className="bg-black"
                                    >
                                        {c.name} · {c.code}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-coral mt-6 w-full justify-center"
                        data-testid="generate-plan-btn"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                {t.teach.generating}
                            </>
                        ) : (
                            <>
                                <Wand2 size={16} /> {t.teach.generate}
                            </>
                        )}
                    </button>
                </form>

                <div
                    className="col-span-12 md:col-span-7"
                    data-testid="plan-output"
                >
                    {!plan && !loading && (
                        <div className="card p-8 h-full flex items-center justify-center text-center no-print">
                            <div>
                                <span className="font-brush text-[var(--lime)] text-5xl -rotate-3 inline-block">
                                    {t.teach.placeholderHead}
                                </span>
                                <p className="font-mono text-sm text-[var(--muted)] mt-3">
                                    {t.teach.placeholderBody}{" "}
                                    <span className="text-[var(--coral)]">
                                        {t.teach.generate}
                                    </span>
                                    .
                                </p>
                            </div>
                        </div>
                    )}
                    {loading && (
                        <div className="card p-8 h-full flex items-center justify-center no-print">
                            <div className="font-mono text-[var(--lime)] flex items-center gap-3">
                                <Loader2 size={18} className="animate-spin" />
                                {t.teach.cooking}
                            </div>
                        </div>
                    )}
                    {plan && (
                        <div id="print-target" className="card p-6 md:p-8">
                            <div className="flex items-center justify-between mb-4 no-print">
                                <span className="tag">{t.teach.tagReady}</span>
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => exportLessonPlanPdf(plan)}
                                        data-testid="plan-pdf"
                                        className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--coral)] hover:text-[var(--lime)] inline-flex items-center gap-2"
                                    >
                                        <FileDown size={14} /> PDF
                                    </button>
                                    <button
                                        onClick={() => window.print()}
                                        data-testid="plan-print"
                                        className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--lime)] hover:text-[var(--cyan)] inline-flex items-center gap-2"
                                    >
                                        <Printer size={14} /> {t.teach.print}
                                    </button>
                                    <button
                                        onClick={copy}
                                        className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--cyan)] hover:text-[var(--lime)] inline-flex items-center gap-2"
                                        data-testid="copy-plan"
                                    >
                                        {copied ? (
                                            <>
                                                <CheckCheck size={14} /> {t.teach.copied}
                                            </>
                                        ) : (
                                            <>
                                                <Copy size={14} /> {t.teach.copyMd}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <MD text={plan.plan_markdown} />
                        </div>
                    )}
                </div>
            </div>
            <PaywallModal
                open={!!paywall}
                onClose={() => setPaywall(null)}
                feature={paywall?.feature}
            />
        </main>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                {label}
            </span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
